import {
  Box,
  Pagination,
  Table,
  CollectionPreferences,
  CollectionPreferencesProps,
  PropertyFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import React, { useEffect, useState } from 'react';

import { getWorkspacesCredentials, listDataPermissions } from 'src/api/permissions';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  defaultWrapLinesPreference,
  i18nStrings,
  mediumPageSizePreference,
  paginationLabels,
} from 'src/commons/tables';
import { createDatabaseDetailLink, createDatasetDetailLink } from 'src/routes';
import { EmptyState } from 'src/commons/EmptyState';
import {
  ANALYTICS_FEDERATION_TIME_OUT,
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_STATUS_ACTIVE,
  LAKE_FORMATION_DATASOURCE_ID,
  OMNI_ROLES_WIKI_URL,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { FlashbarProps } from '@amzn/awsui-components-react-v3/polaris/flashbar/interfaces';
import { keyForCatalogNameMap } from 'src/commons/common';
import { PageHeader } from 'src/components/common/PageHeader';

export interface WorkspaceResourceLinksProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  match: any;
  catalogMap: any;
  setNotifications?: (notifications: ReadonlyArray<FlashbarProps.MessageDefinition>) => void;
}

export const WorkspaceResourceLinks = (props: WorkspaceResourceLinksProps) => {
  const [allItems, setItems] = useState([]);
  const [loadingResource, setLoadingResource] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
  });
  const columnDefinitions = [
    {
      id: 'database',
      header: 'Database name',
      cell: (item) => item.databaseName,
      minWidth: 250,
    },
    {
      id: 'dataset',
      header: 'Dataset name',
      cell: (item) => (item.tableName == undefined ? '*' : item.tableName),
      minWidth: 250,
    },
    {
      id: 'sharedResource',
      header: '' + 'Source resource',
      cell: (item) =>
        item.tableName == undefined ? (
          <Link
            href={createDatabaseDetailLink(
              LAKE_FORMATION_DATASOURCE_ID,
              item?.sourceCatalogId,
              undefined,
              undefined,
              item?.sourceDatabase,
              item?.region,
            )}
          >
            {item.sourceResource}
          </Link>
        ) : (
          <Link
            href={createDatasetDetailLink(
              `DS-glueLF|A-${item?.sourceCatalogId}|DN-${item?.sourceDatabase}|TN-${item?.sourceDataset}|R-${item?.region}`,
            )}
          >
            {item.sourceResource}
          </Link>
        ),
      minWidth: 250,
    },
  ];

  const handleRefresh = async () => {
    setLoadingResource(true);
    let listDataPermissionsRequest = {
      ownerId: props.activeWorkspace.workspaceId,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      nextToken: null,
    };

    let listDataPermissionsResult = await listDataPermissions(listDataPermissionsRequest);
    let permissionList = [...listDataPermissionsResult.dataPermissionList];
    //Loop and get remaining tables
    while (listDataPermissionsResult.nextToken != null) {
      listDataPermissionsRequest.nextToken = listDataPermissionsResult.nextToken;
      listDataPermissionsResult = await listDataPermissions(listDataPermissionsRequest);
      permissionList.push(...listDataPermissionsResult.dataPermissionList);
    }

    let rlList = [];
    for (let permission of permissionList) {
      if (permission.resource?.glueTable !== undefined) {
        let glueTable = permission.resource?.glueTable;
        let sourceResource = glueTable?.targetTable;
        let catalogName = props.catalogMap.get(
          keyForCatalogNameMap(
            LAKE_FORMATION_DATASOURCE_ID,
            glueTable?.catalogId,
            undefined,
            undefined,
            permission?.region,
          ),
        );
        let sourceCatalogName = props.catalogMap.get(
          keyForCatalogNameMap(
            LAKE_FORMATION_DATASOURCE_ID,
            sourceResource?.catalogId,
            undefined,
            undefined,
            permission?.region,
          ),
        );
        rlList.push({
          catalogId: glueTable?.catalogId,
          databaseName: glueTable?.databaseName,
          sourceCatalogId: sourceResource?.catalogId,
          tableName: glueTable?.name,
          sourceResource: `${sourceCatalogName}/${sourceResource?.databaseName}/${sourceResource?.name}`,
          catalogName: catalogName,
          region: permission?.region,
        });
      } else if (permission.resource?.glueDatabase !== undefined) {
        let glueDatabase = permission.resource?.glueDatabase;
        let sourceResource = glueDatabase?.targetDatabase;
        let catalogName = props.catalogMap.get(
          keyForCatalogNameMap(
            LAKE_FORMATION_DATASOURCE_ID,
            glueDatabase?.catalogId,
            undefined,
            undefined,
            permission.region,
          ),
        );
        let sourceCatalogName = props.catalogMap.get(
          keyForCatalogNameMap(
            LAKE_FORMATION_DATASOURCE_ID,
            sourceResource?.catalogId,
            undefined,
            undefined,
            permission.region,
          ),
        );

        rlList.push({
          catalogId: glueDatabase?.catalogId,
          databaseName: glueDatabase?.name,
          sourceCatalogId: sourceResource?.catalogId,
          sourceResource: `${sourceCatalogName}/${sourceResource?.databaseName}/*`,
          catalogName: catalogName,
          region: permission.region,
        });
      }
    }

    for (let resource of rlList) {
      let source = resource.sourceResource;
      const arr = source.split('/');
      resource.sourceDatabase = arr[1];
      resource.sourceDataset = arr[2] == '*' ? undefined : arr[2];
    }
    setItems(rlList);
    setLoadingResource(false);
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    filtering: {
      empty: <EmptyState title={'No resource links'} subtitle={'No resource links were found.'} />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: 'Database name',
          key: 'databaseName',
          groupValuesLabel: 'Database names',
        },
        {
          propertyLabel: 'Dataset name',
          key: 'datasetName',
          groupValuesLabel: 'Dataset names',
        },
        {
          propertyLabel: 'Source resource',
          key: 'sourceResource',
          groupValuesLabel: 'Source resources',
        },
      ],
    },
  });

  const federateIntoWorkspace = async () => {
    if (props.activeWorkspace !== undefined) {
      let response;
      try {
        response = await getWorkspacesCredentials({
          workspaceId: props.activeWorkspace.workspaceId,
          federatedService: 'Athena',
          expirationTimeMins: ANALYTICS_FEDERATION_TIME_OUT,
          getConsoleURL: true,
          getIAMCredentials: false,
        });
        console.log('isengard url:', response);
        window.open(response.consoleURL);
      } catch (err) {
        if (err.name == 'InsufficientPermissionException') {
          props.setNotifications?.([
            {
              type: 'error',
              content: (
                <Box>
                  Insufficient permissions to federate to Athena, make sure you are member of an{' '}
                  <Link external href={OMNI_ROLES_WIKI_URL}>
                    Omni role
                  </Link>{' '}
                  that allows querying data.
                </Box>
              ),
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => props.setNotifications?.([]),
              id: 'insufficientAthenaPermissions',
            },
          ]);
        }
        console.log('Failed to federate into workspace. Reason:', err.message);
      }
    }
  };

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading resource links...'
        loading={loadingResource}
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        empty={<EmptyState title='No Resource links' subtitle='No Resource links were found.' />}
        header={
          <PageHeader
            buttons={[
              {
                text: '',
                icon: 'refresh',
                onItemClick: handleRefresh,
              },
              {
                text: 'Athena query',
                onItemClick: () => {
                  federateIntoWorkspace();
                },
              },
            ]}
            counter={`(${allItems.length})`}
            header={'Resource links created'}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={mediumPageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={loadingResource}
            i18nStrings={i18nStrings}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
      />
    </>
  );
};
